/**
 * Route guard. Replaces the Next.js middleware.ts route protection.
 *
 * Unauthenticated visitors are shown the public marketing landing page (whose
 * CTAs start the Keycloak sign-in / registration flow), instead of being
 * redirected to Keycloak immediately. Authenticated, loading and error states
 * are unchanged.
 */

import { type ReactNode, useMemo } from 'react';
import { useAuth } from 'react-oidc-context';

import { currentRolePolicy, decodeJwtPayload, hasKarexiaAccess } from '@/auth/claims';
import { useServerDeniedKarexiaAccess } from '@/auth/karexiaAccess';
import { NoKarexiaAccess } from '@/auth/NoKarexiaAccess';
import { LandingPage } from '@/routes/landing/LandingPage';
import { useT } from '@/i18n';

export function ProtectedRoute({ children }: { children: ReactNode }) {
  const { t } = useT();
  const auth = useAuth();
  const serverDenied = useServerDeniedKarexiaAccess();
  const accessToken = auth.user?.access_token;
  const sessionHasAccess = useMemo(
    () => hasKarexiaAccess(decodeJwtPayload(accessToken), currentRolePolicy()),
    [accessToken],
  );

  const returnTo = () => window.location.pathname + window.location.search;

  // Still authenticated => keep the app up NO MATTER WHAT. A background
  // automaticSilentRenew can fail transiently (clock skew -> "auth_time in
  // id_token does not match original auth_time", a blocked renew iframe, a brief
  // network drop) while the current access token is still valid. Tearing the
  // whole SPA down to a full-screen error for that (and losing any unsaved form)
  // was BUG-02. As long as we hold a session we render children; the api client
  // refreshes the token on demand on a 401, and the next renew recovers.
  //
  // Shared `core` realm: a signed-in account may belong to another application
  // and hold NO Karexia client role. Such a session gets the friendly
  // "no Karexia access" screen instead of the app (also when the server has
  // refused it with 403 no_karexia_access). Org/community invitation routes are
  // deliberately outside ProtectedRoute, so an invite link still works for them.
  if (auth.isAuthenticated) {
    if (!sessionHasAccess || serverDenied) {
      return <NoKarexiaAccess />;
    }
    return <>{children}</>;
  }

  if (auth.isLoading || auth.activeNavigator) {
    return (
      <div className="flex h-screen items-center justify-center text-slate-500">{t('shell.auth.protectedRoute.signingIn')}</div>
    );
  }

  // Only reachable when we have NO valid session (renew failed AND the token is
  // gone) — a genuine re-login. Preserve where the user was so they land back.
  if (auth.error) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-3 px-6 text-center">
        <p className="text-red-600">{t('shell.auth.protectedRoute.sessionExpiredSignInAgainTo')}</p>
        <button
          className="rounded bg-slate-900 px-4 py-2 text-white"
          onClick={() => void auth.signinRedirect({ state: { returnTo: returnTo() } })}
        >{t('shell.auth.protectedRoute.signInAgain')}</button>
      </div>
    );
  }

  // No session, not loading, no error, no active navigator: show the public
  // marketing landing INSTEAD of an immediate Keycloak redirect. The landing's
  // CTAs call auth.signinRedirect / the Keycloak registration flow, each
  // preserving `returnTo` so a deep-linked visitor still lands where they meant
  // to after logging in. (Previously this branch auto-redirected via an effect.)
  return <LandingPage />;
}
